import { Box, styled } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import {
    useDeleteEligibilityAssignments,
    useEligibilityAssignments,
} from 'hooks'
import { useTranslation } from 'react-i18next'
import useSubcomponents from 'useSubcomponents'
import {
    getFullPathFromUrl,
    useIsSmallScreenFalse,
    useQuery,
} from 'packages/core'
import { Icon } from 'packages/eid-icons'
import { useHistory } from 'react-router'
import config from 'config'
import { Filters, Button } from 'components'
import { useState } from 'react'
import { Select, WarningModal } from 'packages/eid-ui'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import { BulkRemoveButton } from 'components/BulkActionsButtons/BulkRemoveButton'
import { ELIGIBILITY_ASSIGNMENT_TYPES, ELIGIBILITY_TYPES } from 'utils'
import { SearchMenuBarFilter } from 'components/WrapperComponents/SearchMenuBar'

const useButtonStyles = makeStyles((theme) =>
    createStyles({
        root: (isSmallScreen) => ({
            height: '4rem !important',
            borderRadius: '0.4rem',
            padding: '0 1.5rem 0 1rem',
            backgroundColor: theme?.palette?.primary?.main,
            color: '#ffffff',
            textTransform: 'none',
            fontSize: '1.4rem',
            margin: isSmallScreen ? '1.6rem 1.6rem 0 1.6rem' : '',
            '&:hover': {
                backgroundColor: theme?.palette?.primary?.main,
            },
            '& svg': {
                marginRight: '0.8rem',
                '& path': {
                    stroke: '#fff',
                },
            },
        }),
    }),
)

const DeleteButton = styled(Box)({
    paddingRight: '1rem',
    '& button': {
        padding: '0.8rem 1.2rem',
        borderRadius: '0.4rem',
        border: 'solid 1px #ff4d5f',
        backgroundColor: '#fff',
    },
})

const canConfigureEligibilityControlName =
    'ResourceAdmin-SharedFolderDetails-Eligibility-ConfigureEligibility-Control'
const canDeleteEligibilityAssignments =
    'ResourceAdmin-SharedFolderDetails-Eligibility-DeleteEligibility-Control'
const canRemoveMultipleAssignmentsControlName =
    'ResourceAdmin-SharedFolderDetails-Eligibility-DeleteMultipleEligibility-Control'

const Eligibility = ({ roleId, resourceTypeId, resourceId }) => {
    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreenFalse()
    const [eligibilityType, setEligibilityType] = useState(null)

    const dataObject = {
        AssignmentPointId: roleId,
        ResourceTypeId: resourceTypeId,
        EligibilityTypeId: eligibilityType?.id ? eligibilityType.id : null,
        EligibilityAssignmentTypeId: ELIGIBILITY_ASSIGNMENT_TYPES.DIRECT,
    }

    const query = useQuery()
    const history = useHistory()
    const classes = useButtonStyles(isSmallScreen)

    const { list, loading, refetch, pagination } =
        useEligibilityAssignments(dataObject)
    const { hasAccessToControl, getAccessBasedAttributes } = useSubcomponents()
    const [selected, setSelected] = useState([])
    const [deleteAssignments, { isLoading: isDeletingAssignment }] =
        useDeleteEligibilityAssignments(refetch)
    const [selectedItemsToDelete, setSelectedItemsToDelete] = useState(null)

    const attributes = [
        {
            name: 'eligibilityType',
            label: 'EligibilityType',
            sortable: true,
        },
        {
            name: 'assigneeType',
            label: 'AssigneeType',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-Eligibility-AssigneeType',
            },
        },
        {
            name: 'assigneeFriendlyName',
            label: 'Assignee',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-Eligibility-Assignee',
            },
        },
        {
            name: 'resourceTypeFriendlyName',
            label: 'ResourceType',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-Eligibility-ResourceType',
            },
        },

        {
            name: 'eligibilityAssignmentType',
            label: 'AssignmentType',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-Eligibility-AssignmentType',
            },
        },
        {
            hideValue: true,
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <Box display={'flex'} justifyContent={'end'}>
                            {hasAccessToControl(
                                canDeleteEligibilityAssignments,
                            ) && (
                                <DeleteButton>
                                    <Button.Delete
                                        showEndIcon={false}
                                        onClick={() => handleSingleDelete(data)}
                                    />
                                </DeleteButton>
                            )}
                        </Box>
                    )
                },
            },
        },
    ]
    const handleDelete = () => {
        deleteAssignments(selectedItemsToDelete).then(() => {
            setSelectedItemsToDelete(null)
            setSelected([])
        })
    }
    const handleSingleDelete = (data) => {
        setSelectedItemsToDelete([data?.id])
    }

    const handleDeleteBulk = () => {
        setSelectedItemsToDelete(selected)
    }
    const handleRowSelection = (item) => {
        let selectedItems = [...selected]
        const selectedIndex = selected.findIndex((x) => x === item.id)
        if (selectedIndex >= 0) {
            const filteredList = selectedItems.filter((x) => {
                return x !== item.id
            })
            setSelected(filteredList)
        } else {
            setSelected([...selectedItems, item.id])
        }
    }

    const handleSelectAllClick = () => {
        if (selected.length === list.length) {
            setSelected([])
        } else {
            const filteredList = list.map((item) => item.id)
            setSelected(filteredList)
        }
    }

    const selectAllAction = hasAccessToControl(
        canRemoveMultipleAssignmentsControlName,
    )
        ? {
              render: () => {
                  return (
                      <Box style={{ paddingLeft: '3.2rem' }}>
                          <BulkRemoveButton
                              selectedCount={selected.length}
                              label={t('RemoveSelectedAssignments', {
                                  count: selected.length,
                              })}
                              onClick={handleDeleteBulk}
                              dataProtectedsubcomponent={
                                  canRemoveMultipleAssignmentsControlName
                              }
                          />
                      </Box>
                  )
              },
          }
        : null

    return (
        <Box>
            <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="1.6rem 3.2rem"
                bgcolor="white"
                borderTop="1px solid #eef0f4"
            >
                {hasAccessToControl(canConfigureEligibilityControlName) && (
                    <Box
                        data-protectedsubcomponent={
                            canConfigureEligibilityControlName
                        }
                    >
                        {false && (
                            <Button
                                onClick={() => {
                                    query.delete('sharedFolderId')
                                    query.set(
                                        'workflowName',
                                        'ManageGroupWizard',
                                    ) //TODO: Need to change this when workflow name is provided by BE
                                    query.set('SelectedResourceID', resourceId)
                                    query.set('WizardAction', 'publish')
                                    query.set('returnUrl', getFullPathFromUrl())
                                    history.push(
                                        `${
                                            config.APP_SUBPATH
                                        }/sharedFolders/workflows?${query.toString()}`,
                                    )
                                }}
                                className={classes.root}
                            >
                                <Icon name="EditFile" color="#fff" />
                                {t('ConfigureEligibility')}
                            </Button>
                        )}
                    </Box>
                )}

                <Box
                    style={{
                        display: 'flex',
                        gap: '2rem',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        style={{
                            width: '15rem',
                            height: '4rem',
                            marginTop: '-1.6rem',
                        }}
                    >
                        <Select
                            options={ELIGIBILITY_TYPES}
                            onChange={(data) => {
                                setEligibilityType(data)
                            }}
                            value={eligibilityType}
                            getOptionLabel={(option) => t(option.label)}
                            placeholder={t('EligibilityType')}
                            nullableText={t('All')}
                        />
                    </Box>
                    <Box style={{ width: '24rem' }}>
                        <SearchMenuBarFilter>
                            <Filters.TextSearch outlined />
                        </SearchMenuBarFilter>
                    </Box>
                </Box>
            </Box>
            <EidTableGrid
                selectAllAction={selectAllAction}
                rowSelection={hasAccessToControl(
                    canRemoveMultipleAssignmentsControlName,
                )}
                handleSelectAllClick={handleSelectAllClick}
                selected={selected}
                handleClick={handleRowSelection}
                noDataMessage={t('NoDataMessage')}
                pagination={
                    <Box style={{ marginRight: '3.2rem' }}>{pagination}</Box>
                }
                isLoading={loading}
                attributes={getAccessBasedAttributes(attributes)}
                list={list}
                tableViewOnly
            />

            {selectedItemsToDelete && (
                <WarningModal
                    title={t('DeleteEligibilityAssignments')}
                    description={t('AppRoleDeleteMessage')}
                    yesLabel={t('Delete')}
                    noLabel={t('Cancel')}
                    open={selectedItemsToDelete.length}
                    cancel={() => setSelectedItemsToDelete(null)}
                    proceeding={isDeletingAssignment}
                    proceed={handleDelete}
                />
            )}
        </Box>
    )
}

export default Eligibility
